import { isApiError } from '../api/errors'
import { SUPPORT_EMAIL } from '../config'
import { StatusMessage } from './StatusMessage'

type Props = {
  error: unknown
  /** Manual retry. Omitted for errors where retrying cannot help. */
  onRetry?: () => void
  /** Whether a retry is currently in flight. */
  retrying?: boolean
}

/**
 * Maps an API failure to user-facing copy.
 *
 * Rate limiting gets a plain "wait and try again" with a manual button only —
 * automatic retries are exactly what the 60 req/min limit is protecting against.
 */
export function QueryError({ error, onRetry, retrying = false }: Props) {
  const api = isApiError(error) ? error : null
  const kind = api?.kind ?? 'network'

  const retryButton =
    onRetry && kind !== 'not_found' && kind !== 'page_cap' && kind !== 'validation' ? (
      <button type="button" className="button" onClick={onRetry} disabled={retrying}>
        {retrying ? 'Retrying…' : 'Try again'}
      </button>
    ) : undefined

  if (kind === 'rate_limited') {
    return (
      <StatusMessage tone="error" title="Too many requests" action={retryButton}>
        {/* The API's message is written for a human and is safe to show directly. */}
        <p>{api?.detail ?? 'Too many requests. Please slow down and try again shortly.'}</p>
        <p>
          The limit is shared by everyone on your network, so it can be reached without you
          searching heavily.
        </p>
      </StatusMessage>
    )
  }

  // Documented as transient rather than a bad request, so a retry is offered.
  if (kind === 'state_config_unavailable') {
    return (
      <StatusMessage tone="error" title="The state list is temporarily unavailable" action={retryButton}>
        <p>
          The archive could not read its state configuration. This is usually brief — try again in
          a moment.
        </p>
      </StatusMessage>
    )
  }

  if (kind === 'page_cap') {
    return (
      <StatusMessage tone="error" title="You have reached the last available page">
        <p>{api?.detail ?? 'Page is beyond the maximum; narrow the search instead.'}</p>
        <p>
          Add a purchaser or candidate name, a date range, or fewer states to reach the remaining
          records.
        </p>
      </StatusMessage>
    )
  }

  if (kind === 'validation') {
    return (
      <StatusMessage tone="error" title="That search could not be run">
        <p>{api?.detail ?? 'Check the search filters and try again.'}</p>
      </StatusMessage>
    )
  }

  if (kind === 'not_found') {
    return (
      <StatusMessage tone="error" title="Disclosure not found">
        <p>No published disclosure record matches this address.</p>
      </StatusMessage>
    )
  }

  return (
    <StatusMessage tone="error" title="The archive could not be reached" action={retryButton}>
      <p>
        This is a problem with the public archive service, not with your search. Please try again
        in a moment.
      </p>
      <p>
        If it keeps happening, contact <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
      </p>
    </StatusMessage>
  )
}
